import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  orderBy, 
  limit, 
  deleteDoc,
  onSnapshot,
  Timestamp,
  writeBatch,
  where,
  updateDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { toast } from 'react-toastify';

export interface AppNotification {
  id?: string;
  type: 'message' | 'comment' | 'like' | 'mention' | 'review';
  title: string;
  body: string;
  link?: string;
  read: boolean;
  createdAt: Timestamp;
  data?: any;
  icon?: string;
}

class NotificationService {
  private restaurantId: string | null = null;
  private listeners: Set<(notifications: AppNotification[]) => void> = new Set();
  private unsubscribe: (() => void) | null = null;

  // Initialiser avec l'ID du restaurant
  setRestaurantId(restaurantId: string) {
    this.restaurantId = restaurantId;
    this.startListening();
  }

  // Écouter les nouvelles notifications
  private startListening() {
    if (!this.restaurantId) return;
    
    // Se désabonner de l'ancien listener si existant
    if (this.unsubscribe) {
      this.unsubscribe();
    }

    const notifRef = collection(db, 'restaurants', this.restaurantId, 'notifications');
    const q = query(
      notifRef, 
      where('read', '==', false),
      orderBy('createdAt', 'desc'), 
      limit(50)
    );

    this.unsubscribe = onSnapshot(q, (snapshot) => {
      const notifications: AppNotification[] = [];
      
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const data = change.doc.data() as AppNotification;
          const notification = { ...data, id: change.doc.id };
          
          // Afficher un toast pour les nouvelles notifications
          if (data.createdAt.toMillis() > Date.now() - 5000) {
            this.showToast(notification);
          }
          
          notifications.push(notification);
        }
      });

      // Récupérer toutes les notifications non lues
      const allNotifications = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as AppNotification));

      // Notifier tous les listeners
      this.listeners.forEach(callback => callback(allNotifications));
    });
  }

  // Afficher une notification toast
  private showToast(notification: AppNotification) {
    const message = `${notification.title}: ${notification.body}`;
    
    toast(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      className: 'shadow-lg border border-gray-100',
      onClick: () => {
        if (notification.link) {
          window.location.href = notification.link;
        }
      }
    });
  }

  // Ajouter une nouvelle notification
  async addNotification(notification: Omit<AppNotification, 'id' | 'createdAt' | 'read'>): Promise<void> {
    if (!this.restaurantId) throw new Error('Restaurant ID non défini');

    const notifRef = collection(db, 'restaurants', this.restaurantId, 'notifications');
    const newNotif: AppNotification = {
      ...notification,
      read: false,
      createdAt: Timestamp.now()
    };

    await setDoc(doc(notifRef), newNotif);
  }

  // Marquer une notification comme lue
  async markAsRead(notificationId: string): Promise<void> {
    if (!this.restaurantId) throw new Error('Restaurant ID non défini');

    const notifRef = doc(db, 'restaurants', this.restaurantId, 'notifications', notificationId);
    await updateDoc(notifRef, { read: true });
  }

  // Marquer toutes les notifications comme lues
  async markAllAsRead(): Promise<void> {
    if (!this.restaurantId) throw new Error('Restaurant ID non défini');

    const notifRef = collection(db, 'restaurants', this.restaurantId, 'notifications');
    const q = query(notifRef, where('read', '==', false));
    const snapshot = await getDocs(q);

    const promises = snapshot.docs.map(doc => 
      updateDoc(doc.ref, { read: true })
    );

    await Promise.all(promises);
  }

  // S'abonner aux notifications
  subscribe(callback: (notifications: AppNotification[]) => void) {
    this.listeners.add(callback);
    
    // Retourner une fonction de désabonnement
    return () => {
      this.listeners.delete(callback);
    };
  }

  // Obtenir le nombre de notifications non lues
  async getUnreadCount(): Promise<number> {
    if (!this.restaurantId) return 0;

    const notifRef = collection(db, 'restaurants', this.restaurantId, 'notifications');
    const q = query(notifRef, where('read', '==', false));
    const snapshot = await getDocs(q);
    
    return snapshot.size;
  }

  // Nettoyer les vieilles notifications (plus de 30 jours)
  async cleanOldNotifications(): Promise<void> {
    if (!this.restaurantId) return;

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const notifRef = collection(db, 'restaurants', this.restaurantId, 'notifications');
    const q = query(
      notifRef, 
      where('createdAt', '<', Timestamp.fromDate(thirtyDaysAgo))
    );
    
    const snapshot = await getDocs(q);
    const promises = snapshot.docs.map(doc => 
      deleteDoc(doc.ref)
    );
    await Promise.all(promises);
  }

  // Créer une notification pour un nouveau message
  createMessageNotification(senderName: string, message: string, conversationId: string) {
    return this.addNotification({
      type: 'message',
      title: `Nouveau message de ${senderName}`,
      body: message,
      link: `/messaging?conversation=${conversationId}`,
      icon: '💬'
    });
  }

  // Créer une notification pour un like
  createLikeNotification(userName: string, postTitle: string) {
    return this.addNotification({
      type: 'like',
      title: `${userName} a aimé votre publication`,
      body: postTitle,
      link: '/social?tab=activity',
      icon: '👍'
    });
  }

  // Créer une notification pour un commentaire
  createCommentNotification(userName: string, comment: string, postId: string) {
    return this.addNotification({
      type: 'comment',
      title: `${userName} a commenté votre publication`,
      body: comment,
      link: `/social?tab=activity&post=${postId}`,
      icon: '💭'
    });
  }

  // Créer une notification pour une mention
  createMentionNotification(userName: string, context: string) {
    return this.addNotification({
      type: 'mention',
      title: `${userName} vous a mentionné`,
      body: context,
      link: '/social',
      icon: '@'
    });
  }

  // Créer une notification pour un avis
  createReviewNotification(userName: string, rating: number, comment: string) {
    return this.addNotification({
      type: 'review',
      title: `Nouvel avis ${rating}⭐ de ${userName}`,
      body: comment,
      link: '/reviews',
      icon: '⭐'
    });
  }

  // Cleanup
  cleanup() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.listeners.clear();
  }
}

const notificationService = new NotificationService();

export default notificationService;
export type { AppNotification as Notification };
